import { useScopedOrganizationId } from "@/contexts/WorkspaceOrgContext";
import { useAuth } from "@/contexts/AuthContext";
import { canAccessAudienceDataUi } from "@/lib/ssd/ssdAudienceRoles";
import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";
import { ghostBtnClass } from "@/components/common/surfaceClasses";
import { useAudienceData } from "@/components/audience-data/useAudienceData";
import { AudienceDataSettingsMenu } from "@/components/audience-data/AudienceDataSettingsMenu";
import { AudienceDataExportCard } from "@/components/audience-data/AudienceDataExportCard";
import { AudienceDataInboundCard } from "@/components/audience-data/AudienceDataInboundCard";
import { AudienceDataSalesCard } from "@/components/audience-data/AudienceDataSalesCard";
import { AudienceDataDealerMapCard } from "@/components/audience-data/AudienceDataDealerMapCard";
import { AudienceDataAggregationStrip } from "@/components/audience-data/AudienceDataAggregationStrip";

/** Manager-bucket Audience Data: Sales Feed ingest + date-range sale-fact list. */
export function AudienceDataPanel() {
  const auth = useAuth();
  const scopedOrgId = useScopedOrganizationId();
  const canAccess = canAccessAudienceDataUi(auth);
  const vm = useAudienceData(scopedOrgId, canAccess && Boolean(scopedOrgId));

  if (!canAccess) {
    return (
      <div className="w-full min-w-0 space-y-3 text-left">
        <h2 className="text-lg font-bold text-gray-900 dark:text-zinc-100">Audience Data</h2>
        <p className="text-sm text-gray-500 dark:text-zinc-400">
          Audience Data is available to marketing, admin, and owner roles.
        </p>
      </div>
    );
  }

  if (!scopedOrgId) {
    return (
      <div className="w-full min-w-0 space-y-3 text-left">
        <h2 className="text-lg font-bold text-gray-900 dark:text-zinc-100">Audience Data</h2>
        <p className="text-sm text-gray-500 dark:text-zinc-400">Select a workspace organization first.</p>
      </div>
    );
  }

  return (
    <div
      {...{ [FIREFLY_TARGET_ATTR]: "audience-data-surface" }}
      className="w-full min-w-0 space-y-5 overflow-x-hidden text-left"
    >
      <div className="flex min-w-0 flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-lg font-bold text-gray-900 dark:text-zinc-100">Audience Data</h2>
          <p className="mt-1 text-sm text-gray-600 dark:text-zinc-300">
            Ingest Sales Feed CSVs into shared sale facts for Google and Meta audiences. Manufacturer views
            hide end-customer identity unless the selling dealer has consented.
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <AudienceDataSettingsMenu
            links={vm.consent}
            sharing={vm.sharing}
            onSetShare={vm.setShareEndCustomers}
          />
          <button
            type="button"
            className={ghostBtnClass}
            onClick={() => void vm.reload()}
            disabled={
              vm.loading ||
              vm.ingesting ||
              vm.sharing ||
              vm.acknowledging ||
              vm.mapping ||
              vm.exporting !== null ||
              vm.rotatingFeed !== null
            }
          >
            {vm.loading ? "Loading…" : "Refresh"}
          </button>
        </div>
      </div>

      {vm.error ? (
        <p className="text-sm text-red-700 dark:text-red-300" role="alert">
          {vm.error}
        </p>
      ) : null}
      {vm.ingestMessage ? (
        <p className="text-sm text-gray-700 dark:text-zinc-300">{vm.ingestMessage}</p>
      ) : null}

      <AudienceDataAggregationStrip aggregation={vm.aggregation} />
      <AudienceDataInboundCard
        inbound={vm.inbound}
        newSales={vm.facts.filter((f) => f.is_new)}
        newCount={vm.feed.new_count}
        ingesting={vm.ingesting}
        latestJob={vm.latestJob}
        onFile={(file) => void vm.ingestFile(file)}
        acknowledging={vm.acknowledging}
        onAcknowledgeNew={vm.acknowledgeNew}
      />
      <AudienceDataDealerMapCard board={vm.dealerMap} mapping={vm.mapping} onMap={vm.mapDealer} />
      <AudienceDataSalesCard
        facts={vm.facts}
        issues={vm.issues}
        quality={vm.quality}
        skippedLastJob={vm.latestJob?.skipped_count ?? 0}
        newCount={vm.feed.new_count}
        dateFrom={vm.dateFrom}
        dateTo={vm.dateTo}
        setDateFrom={vm.setDateFrom}
        setDateTo={vm.setDateTo}
        acknowledging={vm.acknowledging}
        onAcknowledgeNew={vm.acknowledgeNew}
      />
      <AudienceDataExportCard
        exporting={vm.exporting}
        latestExport={vm.latestExport}
        conversions={vm.googleConversions}
        savingConversions={vm.savingConversions}
        onSelectConversion={(name) => void vm.saveGoogleConversion({ selected: name })}
        onAddConversion={(name) => void vm.saveGoogleConversion({ add: name })}
        onExport={(dest) => void vm.exportAudience(dest)}
        hostedFeeds={vm.destinationFeeds}
        hostedTokens={vm.hostedTokens}
        rotatingFeed={vm.rotatingFeed}
        onRotateFeed={(dest) => void vm.rotateHostedFeed(dest)}
      />
    </div>
  );
}
